import { useState, useEffect } from "react";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import {
  Users, Plus, Trash2, Loader2, Building2, Mail, Phone,
  UserCheck, X, Shield
} from "lucide-react";

const MANDATORY_ROLES = [
  { id: "sponsor", label: "Client Sponsor", required: true },
  { id: "representative", label: "Client Representative", required: true },
  { id: "pm_coordinator", label: "PM / Coordinator", required: true },
  { id: "auditor", label: "Lead Auditor", required: false },
  { id: "quality_manager", label: "Quality Manager", required: false },
  { id: "technical_expert", label: "Technical Expert", required: false },
];

const ContactsPage = () => {
  const { t } = useTranslation('platform');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", role: "sponsor", email: "", phone: "", company: "" });

  useEffect(() => { fetchContacts(); }, []);

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/contacts`, { withCredentials: true });
      setContacts(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.name || !form.email) return;
    try {
      await axios.post(`${API}/contacts`, form, { withCredentials: true });
      setShowForm(false);
      setForm({ name: "", role: "sponsor", email: "", phone: "", company: "" });
      fetchContacts();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/contacts/${id}`, { withCredentials: true });
      fetchContacts();
    } catch (e) { console.error(e); }
  };

  const roleColors = {
    sponsor: "#4ADE80", representative: "#38BDF8", pm_coordinator: "#FBBF24",
    auditor: "#E8A838", quality_manager: "#A78BFA", technical_expert: "#F472B6",
  };

  const getRoleLabel = (roleId) => MANDATORY_ROLES.find(r => r.id === roleId)?.label || roleId;

  // Check which mandatory roles are filled
  const filledRoles = new Set(contacts.map(c => c.role));

  return (
    <QMSLayout title={t('platform:contacts.title')}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Syne'] text-2xl font-bold text-white" data-testid="contacts-title">Project Contacts</h2>
          <p className="text-[#8B95A5] text-sm mt-1">Mandatory and optional project team contacts</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="add-contact-btn">
          <Plus size={16} /> Add Contact
        </button>
      </div>

      {/* Mandatory Roles Status */}
      <div className="qms-card mb-6" data-testid="mandatory-roles">
        <div className="qms-card-body">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <Shield size={16} className="text-[#E8A838]" /> Mandatory Roles
          </h3>
          <div className="flex flex-wrap gap-3">
            {MANDATORY_ROLES.filter(r => r.required).map(role => (
              <div key={role.id} className={`flex items-center gap-2 px-3 py-2 rounded text-xs font-medium ${
                filledRoles.has(role.id) ? "bg-[#4ADE80]/10 text-[#4ADE80]" : "bg-[#F87171]/10 text-[#F87171]"
              }`}>
                {filledRoles.has(role.id) ? <UserCheck size={14} /> : <X size={14} />}
                {role.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showForm && (
        <div className="qms-card mb-6" data-testid="contact-form">
          <div className="qms-card-body space-y-3">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Plus size={16} className="text-[#36A9E1]" /> New Contact
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input type="text" placeholder="Full Name" value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="qms-input" data-testid="contact-name" />
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                className="qms-input" data-testid="contact-role">
                {MANDATORY_ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
              <input type="email" placeholder="Email" value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                className="qms-input" data-testid="contact-email" />
              <input type="tel" placeholder="Phone (optional)" value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                className="qms-input" data-testid="contact-phone" />
              <input type="text" placeholder="Company" value={form.company}
                onChange={e => setForm(p => ({ ...p, company: e.target.value }))}
                className="qms-input" data-testid="contact-company" />
            </div>
            <div className="flex gap-3">
              <button onClick={handleCreate} className="btn-primary" data-testid="contact-save-btn">Save Contact</button>
              <button onClick={() => setShowForm(false)} className="btn-ghost">{t('common:actions.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Contacts Grid */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={24} /></div>
      ) : contacts.length === 0 ? (
        <div className="qms-card"><div className="qms-card-body text-center text-[#8B95A5] py-12">
          <Users size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No contacts yet. Add mandatory project contacts to get started.</p>
        </div></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {contacts.map(c => (
            <div key={c.contact_id} className="qms-card" data-testid={`contact-${c.contact_id}`}>
              <div className="qms-card-body">
                <div className="flex items-center justify-between mb-2">
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{
                    color: roleColors[c.role] || "#8B95A5",
                    backgroundColor: `${roleColors[c.role] || "#8B95A5"}20`
                  }}>{getRoleLabel(c.role)}</span>
                  <button onClick={() => handleDelete(c.contact_id)} className="text-[#64748B] hover:text-[#F87171] transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
                <h4 className="text-white font-medium text-sm">{c.name}</h4>
                {c.company && <p className="text-[#8B95A5] text-xs flex items-center gap-1 mt-1"><Building2 size={12} />{c.company}</p>}
                <div className="mt-2 space-y-0.5">
                  <p className="text-[#36A9E1] text-xs flex items-center gap-1"><Mail size={12} />{c.email}</p>
                  {c.phone && <p className="text-[#8B95A5] text-xs flex items-center gap-1"><Phone size={12} />{c.phone}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </QMSLayout>
  );
};

export default ContactsPage;
